import { getWeddingDisplayPrice, resolveDiscoveryPrice, type DiscoveryPriceSource } from "@workspace/booking-rules";

export type RentalType = "driving" | "chauffeured" | "event" | "wedding";

export type DisplayPriceCar = DiscoveryPriceSource;

/**
 * Unit for the primary "from" price in no-rental-type mode. `day` and
 * `hour` map to the `common.per_day` / `common.per_hour` copy keys; `flat`
 * renders no suffix (minimum-charge style prices).
 */
export type DisplayPriceUnit = "day" | "hour" | "flat";

export interface DisplayPriceResult {
  price: number | null;
  /**
   * Legacy raw suffix ("/day" | "") consumed by the per-rental-type
   * callers (e.g. favourites SavedTile). No-rental-type callers should
   * prefer `unit` so the suffix can be translated.
   */
  suffix: string;
  /**
   * No-rental-type mode only: semantic unit for the primary price so the
   * card can render a translated suffix.
   */
  unit?: DisplayPriceUnit;
  /**
   * No-rental-type mode only: a secondary hourly rate — the LOWEST of the
   * event and chauffeur hourly rates (whichever is cheaper) — shown on its
   * own muted line beneath the headline. Null when the headline is already
   * the hourly rate (promoted) or none is available.
   */
  hourlyPrice?: number | null;
}

/**
 * Shared "from £X / day" price resolver used by every car tile (home
 * page CarCard, favourites SavedTile, etc.) so the same listing never
 * shows "Price on request" on one page and a real price on another
 * (task #2360). Walks the rental-type-specific waterfall over every
 * price column the listing might have set, falling back across rental
 * modes so a chauffeur-only listing still surfaces its hourly minimum
 * on the default-rental tiles.
 *
 * Inactive-rental gating (May 2026 — car #1059383 incident): each
 * waterfall step is gated on the matching `rental_*_active` flag so a
 * price stuck in a disabled rental mode's column (e.g. a host who
 * later turned event hire off but left `price_event` populated, or a
 * keysmash typo that survived an earlier draft) can never hijack the
 * card. `minimumPrice` and the symmetric driving/event fallbacks fire
 * only when their *destination* rental mode is the one being
 * requested; cross-mode fallbacks require the destination mode to be
 * `rental_*_active`. Listings with no active rental mode return
 * `{ price: null }` so the card falls back to "Price on request".
 */
export function getDisplayPrice(
  car: DisplayPriceCar,
  rentalType?: RentalType,
): DisplayPriceResult {
  const drivingOn = !!car.rentalDrivingActive;
  const chauffeurOn = !!car.rentalChauffeurActive;
  const eventOn = !!car.rentalEventActive;
  switch (rentalType) {
    case "wedding": {
      const wedding = getWeddingDisplayPrice({
        year: 0,
        make: "",
        rentalEventActive: eventOn,
        rentalChauffeuredActive: chauffeurOn,
        priceEvent: car.priceEvent,
        eventMinPrice: car.eventMinPrice,
        eventHourlyRate: car.eventHourlyRate,
        chauffeuredMinPrice: car.chauffeurMinPrice,
        chauffeuredHourlyRate: car.chauffeuredHourlyRate,
        minimumPrice: car.minimumPrice,
      });
      if (!wedding) return { price: null, suffix: "", unit: "flat", hourlyPrice: null };
      return {
        price: wedding.price,
        suffix: wedding.unit === "day" ? "/day" : wedding.unit === "hour" ? "/hr" : "",
        unit: wedding.unit === "minimum" ? "flat" : wedding.unit,
        hourlyPrice: wedding.hourlyPrice,
      };
    }
    case "chauffeured": {
      // Secondary muted hourly line under the "from" minimum headline
      // (task #6107): gated on the chauffeur rental mode being active,
      // same as the headline itself.
      const chauffeurHourly =
        chauffeurOn && car.chauffeuredHourlyRate != null && car.chauffeuredHourlyRate > 0
          ? car.chauffeuredHourlyRate
          : null;
      if (chauffeurOn && car.chauffeurMinPrice != null && car.chauffeurMinPrice > 0) return { price: car.chauffeurMinPrice, suffix: "", hourlyPrice: chauffeurHourly };
      if (chauffeurOn && car.minimumPrice != null && car.minimumPrice > 0) return { price: car.minimumPrice, suffix: "", hourlyPrice: chauffeurHourly };
      if (drivingOn && car.priceDriving != null && car.priceDriving > 0) return { price: car.priceDriving, suffix: "/day" };
      if (eventOn && car.priceEvent != null && car.priceEvent > 0) return { price: car.priceEvent, suffix: "/day" };
      if (eventOn && car.eventMinPrice != null && car.eventMinPrice > 0) return { price: car.eventMinPrice, suffix: "" };
      return { price: null, suffix: "" };
    }
    case "event": {
      // Secondary muted hourly line under the "from" minimum headline
      // (task #6107): gated on the event rental mode being active. Not
      // attached to the "/day" fallbacks below — the daily rate already
      // carries its own unit.
      const eventHourly =
        eventOn && car.eventHourlyRate != null && car.eventHourlyRate > 0
          ? car.eventHourlyRate
          : null;
      if (eventOn && car.eventMinPrice != null && car.eventMinPrice > 0) return { price: car.eventMinPrice, suffix: "", hourlyPrice: eventHourly };
      if (eventOn && car.minimumPrice != null && car.minimumPrice > 0) return { price: car.minimumPrice, suffix: "", hourlyPrice: eventHourly };
      if (eventOn && car.priceEvent != null && car.priceEvent > 0) return { price: car.priceEvent, suffix: "/day" };
      if (drivingOn && car.priceDriving != null && car.priceDriving > 0) return { price: car.priceDriving, suffix: "/day" };
      if (chauffeurOn && car.chauffeurMinPrice != null && car.chauffeurMinPrice > 0) return { price: car.chauffeurMinPrice, suffix: "" };
      return { price: null, suffix: "" };
    }
    case "driving": {
      if (drivingOn && car.priceDriving != null && car.priceDriving > 0) return { price: car.priceDriving, suffix: "/day" };
      if (eventOn && car.priceEvent != null && car.priceEvent > 0) return { price: car.priceEvent, suffix: "/day" };
      // `minimumPrice` is a generic "minimum charge" fallback, not a
      // driving-specific column — surface it whenever **any** rental
      // mode is active. Without this, chauffeur-only listings that
      // legacy-use `minimum_price` would drop to "Price on request"
      // on the default-rental tiles (architect review feedback,
      // car #1059383 fix).
      const anyRentalOn = drivingOn || chauffeurOn || eventOn;
      if (anyRentalOn && car.minimumPrice != null && car.minimumPrice > 0) return { price: car.minimumPrice, suffix: "" };
      if (chauffeurOn && car.chauffeurMinPrice != null && car.chauffeurMinPrice > 0) return { price: car.chauffeurMinPrice, suffix: "" };
      if (eventOn && car.eventMinPrice != null && car.eventMinPrice > 0) return { price: car.eventMinPrice, suffix: "" };
      return { price: null, suffix: "" };
    }
    default:
      // No rental type selected (browsing surfaces: search with no filter,
      // home rails, nearby rail). The "from" headline is the LOWEST active
      // daily/minimum price; a chauffeur/event hourly rate is shown as a
      // secondary line, or promoted to the headline when no daily/minimum
      // price exists.
      return getNoRentalTypeDisplayPrice(car, drivingOn, chauffeurOn, eventOn);
  }
}

function getNoRentalTypeDisplayPrice(
  car: DisplayPriceCar,
  drivingOn: boolean,
  chauffeurOn: boolean,
  eventOn: boolean,
): DisplayPriceResult {
  const { primary, hourly } = resolveDiscoveryPrice({
    ...car,
    rentalDrivingActive: drivingOn,
    rentalChauffeurActive: chauffeurOn,
    rentalEventActive: eventOn,
  });
  return {
    price: primary?.amount ?? null,
    unit: primary?.unit ?? "flat",
    suffix: primary?.unit === "day" ? "/day" : primary?.unit === "hour" ? "/hr" : "",
    hourlyPrice: hourly?.amount ?? null,
  };
}
